import { useCallback, useEffect, useState } from "react";
import NoticeTicker from "./components/NoticeTicker";
import SiteHeader from "./components/SiteHeader";
import HeroSection from "./components/HeroSection";
import TopicWall from "./components/TopicWall";
import LatestLeaks from "./components/LatestLeaks";
import HotDiscussion from "./components/HotDiscussion";
import CommentWall from "./components/CommentWall";
import SectionGrid from "./components/SectionGrid";
import SubmitFlow from "./components/SubmitFlow";
import SourceNote from "./components/SourceNote";
import SiteFooter from "./components/SiteFooter";
import SubmitModal from "./components/SubmitModal";
import BackToTop from "./components/BackToTop";
import Toast from "./components/Toast";

export default function Home() {
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [submitOpen, setSubmitOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; tone: "success" | "error" } | null>(null);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 3200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const scrollToLeaks = useCallback(() => {
    window.setTimeout(() => {
      const target = document.getElementById("leaks");
      if (!target) return;
      const top = target.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: "smooth" });
    }, 60);
  }, []);

  const handleSelectTag = useCallback(
    (label: string) => {
      setActiveTag((prev) => (prev === label ? null : label));
      setQuery("");
      scrollToLeaks();
    },
    [scrollToLeaks],
  );

  const handleSearch = useCallback(
    (value: string) => {
      setQuery(value);
      setActiveTag(null);
      scrollToLeaks();
    },
    [scrollToLeaks],
  );

  const handleClear = useCallback(() => {
    setActiveTag(null);
    setQuery("");
  }, []);

  const handleFormSubmit = useCallback((summary: string) => {
    if (summary.startsWith("error:")) {
      setToast({ message: summary.replace("error:", ""), tone: "error" });
      return;
    }
    setToast({ message: summary, tone: "success" });
  }, []);

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <NoticeTicker />
      <SiteHeader onSearch={handleSearch} onOpenSubmit={() => setSubmitOpen(true)} />

      <main className="w-full flex-1">
        <HeroSection onOpenSubmit={() => setSubmitOpen(true)} />
        <TopicWall activeTag={activeTag} onSelectTag={handleSelectTag} />
        <LatestLeaks
          activeTag={activeTag}
          query={query}
          onSelectTag={handleSelectTag}
          onClearTag={handleClear}
        />
        <HotDiscussion />
        <CommentWall />
        <SectionGrid />
        <SubmitFlow onSubmit={handleFormSubmit} />
        <SourceNote />
      </main>

      <SiteFooter onNotify={(message) => setToast({ message, tone: "success" })} />
      <BackToTop />
      <SubmitModal
        open={submitOpen}
        onClose={() => setSubmitOpen(false)}
        onSubmit={handleFormSubmit}
      />
      <Toast message={toast?.message ?? null} tone={toast?.tone ?? "success"} />
    </div>
  );
}